import { registerPlugin } from "@capacitor/core";

import type { CustomerIoGeofencePlugin, LocationPermissionStatus } from "./definitions";

/**
 * The web implementation is a no-op: browsers have no background region monitoring, so there is
 * nothing to fall back to. `isAvailable()` returning false is how callers know to hide the flow.
 */
const CustomerIoGeofence = registerPlugin<CustomerIoGeofencePlugin>("CustomerIoGeofence", {
  web: async () => {
    const { CustomerIoGeofenceWeb } = await import("./web");
    return new CustomerIoGeofenceWeb();
  },
});

/**
 * Whether this build can actually do geofencing.
 *
 * The web bundle updates independently of the native binary, so a patient can be running today's
 * JavaScript inside an app installed before this plugin existed. Capacitor rejects calls to a
 * plugin the binary does not contain, so every entry point goes through here and treats a rejection
 * as "not available" rather than surfacing an error to someone who simply has an older app.
 * Getting those patients onto a build that has the plugin is AppVersionCheck's job.
 */
export const isGeofenceAvailable = async (): Promise<boolean> => {
  try {
    const { available } = await CustomerIoGeofence.isAvailable();
    return available;
  } catch {
    return false;
  }
};

/**
 * Configure the native SDK. Safe to call on every startup; the native side only builds its
 * configuration once. Ignored on builds without the plugin.
 */
export const initializeGeofencing = async (cdpApiKey: string): Promise<void> => {
  if (!cdpApiKey) {
    return;
  }

  try {
    await CustomerIoGeofence.initialize({ cdpApiKey });
  } catch {
    // Older binary, or web: nothing to configure.
  }
};

/** Identify, ignoring builds without the plugin. Safe to call on every login. */
export const identifyForGeofencing = async (personId: string): Promise<void> => {
  try {
    await CustomerIoGeofence.identify({ personId });
  } catch {
    // Older binary: nothing to identify against.
  }
};

/** Clear the identified profile, ignoring builds without the plugin. */
export const clearGeofencingIdentity = async (): Promise<void> => {
  try {
    await CustomerIoGeofence.clearIdentify();
  } catch {
    // Older binary: nothing to clear.
  }
};

/** Current permission state, reported as "denied" when the build cannot do geofencing at all. */
export const checkGeofencePermissions = async (): Promise<LocationPermissionStatus> => {
  try {
    return await CustomerIoGeofence.checkLocationPermissions();
  } catch {
    return { location: "denied" };
  }
};

export * from "./definitions";
export { CustomerIoGeofence };
